/**
 * 지금 걸려 있는 필터를 칩으로 보여 준다.
 *
 * **값을 글자로 다시 쓰지 않는다.** 카드에 쓰는 조각을 그대로 얹어서,
 * 「어떤 카드가 남았나」와 「무엇으로 걸렀나」가 같은 그림으로 읽힌다.
 *
 * 축 이름(「담당자」·「타입」)도 안 적는다 — 아바타가 붙어 있으면 담당자이고
 * 도형이 붙어 있으면 타입이다. 붙일 자리가 없는 검색어에만 돋보기를 준다.
 */

import type { Label, TicketFilter, UserBrief } from '../api';
import { deviceLabel, environmentLabel, KINDS, priorityOf, typeOf } from '../columns';
import { AssigneeMark, LabelChip, PriorityMark, TypeMark } from './Chips';

function Chip({
  onClear,
  label,
  children,
}: {
  onClear: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <span className="inline-flex min-h-s6 items-center gap-s2 rounded-md border border-line bg-sunk py-[1px] pr-[1px] pl-s3 text-meta max-[720px]:min-h-[44px]">
      {children}
      {/* × 는 칩 안에서 **자기 몫의 상자**를 갖는다. 글자에 붙여 두면
          좁은 화면에서 값과 지우기가 같은 손끝에 걸린다. */}
      <button
        type="button"
        onClick={onClear}
        aria-label={`${label} 필터 지우기`}
        className="grid size-s5 shrink-0 place-items-center rounded-sm text-ink-3 transition-colors hover:bg-panel hover:text-ink max-[720px]:size-s8"
      >
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
          <path
            d="M2.5 2.5l6 6M8.5 2.5l-6 6"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </span>
  );
}

export function FilterChips({
  filter,
  onFilter,
  members,
  labels,
  me,
}: {
  filter: TicketFilter;
  onFilter: (next: TicketFilter) => void;
  members: UserBrief[];
  labels: Label[];
  /** 지금 쓰는 사람. 자기 칩은 이름 대신 「나」로 적는다. */
  me?: number;
}) {
  function clear<K extends keyof TicketFilter>(key: K) {
    onFilter({ ...filter, [key]: null });
  }

  const kind = filter.kind ? KINDS.find((k) => k.id === filter.kind) : undefined;
  const assignee = members.find((m) => m.id === filter.assignee_id) ?? null;
  const reporter = members.find((m) => m.id === filter.reporter_id) ?? null;
  const type = typeOf(filter.type ?? null);
  const priority = priorityOf(filter.priority ?? null);
  const label = labels.find((l) => l.id === filter.label_id) ?? null;

  return (
    <>
      {kind && (
        <Chip onClear={() => clear('kind')} label={kind.label}>
          {kind.label}
        </Chip>
      )}
      {filter.device && (
        <Chip onClear={() => clear('device')} label={deviceLabel(filter.device) ?? ''}>
          {deviceLabel(filter.device)}
        </Chip>
      )}
      {filter.environment && (
        <Chip
          onClear={() => clear('environment')}
          label={environmentLabel(filter.environment) ?? ''}
        >
          {environmentLabel(filter.environment)}
        </Chip>
      )}
      {/* 담당자·작성자는 같은 사람 그림이라 **축 이름을 앞에 붙인다**
          (2026-10-19 — 작성자도 사람을 고르게 되면서). */}
      {assignee && (
        <Chip onClear={() => clear('assignee_id')} label={`담당 ${assignee.display_name}`}>
          <span className="text-ink-3">담당</span>
          <AssigneeMark user={assignee} size={18} />
          {assignee.id === me ? '나' : assignee.display_name}
        </Chip>
      )}
      {reporter && (
        <Chip onClear={() => clear('reporter_id')} label={`작성 ${reporter.display_name}`}>
          <span className="text-ink-3">작성</span>
          <AssigneeMark user={reporter} size={18} />
          {reporter.id === me ? '나' : reporter.display_name}
        </Chip>
      )}
      {filter.held != null && (
        /* 보류(2026-09-02). **`!= null` 로 봐야 한다** — `false`(미룬 것
           빼고)도 사람이 고른 값이라, 다른 칩처럼 `filter.held &&` 로 쓰면
           그 걸음에서 칩이 통째로 안 뜬다. 걸려 있는데 아무 표시가 없으면
           「티켓이 사라졌다」로 읽힌다.

           붙일 그림이 없어 **검색어처럼 글자만** 둔다. 지우면 축이 풀려
           서버 기본(전부 준다)으로 돌아간다. */
        <Chip
          onClear={() => clear('held')}
          label={filter.held ? '보류만' : '보류 숨김'}
        >
          {filter.held ? '보류만' : '보류 숨김'}
        </Chip>
      )}
      {type && (
        <Chip onClear={() => clear('type')} label={type.label}>
          <TypeMark type={type.id} />
          {type.label}
        </Chip>
      )}
      {priority && (
        <Chip onClear={() => clear('priority')} label={priority.label}>
          <PriorityMark priority={priority.id} />
        </Chip>
      )}
      {label && (
        <Chip onClear={() => clear('label_id')} label={label.name}>
          <LabelChip label={label} />
        </Chip>
      )}
      {filter.q ? (
        <Chip onClear={() => clear('q')} label={`검색 ${filter.q}`}>
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <circle cx="6.2" cy="6.2" r="4.4" stroke="currentColor" strokeWidth="1.3" />
            <path d="m9.6 9.6 2.6 2.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
          {filter.q}
        </Chip>
      ) : null}
    </>
  );
}
